import {useCallback, useEffect, useRef, useState} from "react";
import {useMediaQuery} from "@mui/material";

const DEFAULT_MIN_HEIGHT = 220;
const DEFAULT_MAX_HEIGHT_VH = 0.75;
const DEFAULT_HEIGHT_VH = 0.4;
const DEFAULT_CLOSE_THRESHOLD = 180;

const getMaxHeight = (minHeight: number, maxHeightVh: number) => Math.max(
    minHeight,
    Math.round(window.innerHeight * maxHeightVh),
);

const clampHeight = (height: number, minHeight: number, maxHeightVh: number) => Math.min(
    Math.max(height, minHeight),
    getMaxHeight(minHeight, maxHeightVh),
);

export const useMobileResizableDrawer = () => {
    const isMobile = useMediaQuery('(max-width:900px)');
    const getDefaultHeight = useCallback(() => clampHeight(
        Math.round(window.innerHeight * DEFAULT_HEIGHT_VH),
        DEFAULT_MIN_HEIGHT,
        DEFAULT_MAX_HEIGHT_VH,
    ), []);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerHeight, setDrawerHeight] = useState(() => getDefaultHeight());
    const [isResizingDrawer, setIsResizingDrawer] = useState(false);
    const drawerHeightRef = useRef(drawerHeight);
    const drawerResizeRef = useRef({
        startY: 0,
        startHeight: drawerHeight,
        active: false,
    });

    useEffect(() => {
        if (!isMobile) setDrawerOpen(false);
    }, [isMobile]);

    useEffect(() => {
        // The move/end listeners run outside React's event system, so keep the
        // latest height in a ref to avoid stale values during an active drag.
        drawerHeightRef.current = drawerHeight;
    }, [drawerHeight]);

    useEffect(() => {
        if (!isMobile) {
            setDrawerHeight(getDefaultHeight());
            return;
        }

        const handleResize = () => {
            setDrawerHeight(current => clampHeight(current, DEFAULT_MIN_HEIGHT, DEFAULT_MAX_HEIGHT_VH));
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [getDefaultHeight, isMobile]);

    const closeDrawer = useCallback(() => {
        setDrawerOpen(false);
    }, []);

    const handleOpenDrawer = useCallback(() => {
        setDrawerHeight(current => clampHeight(current, DEFAULT_MIN_HEIGHT, DEFAULT_MAX_HEIGHT_VH));
        setDrawerOpen(true);
    }, []);

    const resetDrawerHeight = useCallback(() => {
        setDrawerHeight(getDefaultHeight());
    }, [getDefaultHeight]);

    const finishDrawerResize = useCallback((height: number) => {
        drawerResizeRef.current.active = false;
        setIsResizingDrawer(false);

        if (height <= DEFAULT_CLOSE_THRESHOLD) {
            setDrawerOpen(false);
            return;
        }

        setDrawerHeight(clampHeight(height, DEFAULT_MIN_HEIGHT, DEFAULT_MAX_HEIGHT_VH));
    }, []);

    const handleDrawerResizeStart = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        event.preventDefault();

        drawerResizeRef.current = {
            startY: event.clientY,
            startHeight: drawerHeightRef.current,
            active: true,
        };
        setIsResizingDrawer(true);
    }, []);

    useEffect(() => {
        if (!isResizingDrawer) return;

        // Resize tracking has to stay attached even if the pointer leaves the
        // handle or the drawer while the user is dragging.
        const handlePointerMove = (event: PointerEvent) => {
            if (!drawerResizeRef.current.active) return;

            const deltaY = event.clientY - drawerResizeRef.current.startY;
            const nextHeight = Math.min(
                getMaxHeight(DEFAULT_MIN_HEIGHT, DEFAULT_MAX_HEIGHT_VH),
                Math.max(0, drawerResizeRef.current.startHeight - deltaY),
            );

            setDrawerHeight(nextHeight);
        };

        const handlePointerEnd = () => {
            if (!drawerResizeRef.current.active) return;
            finishDrawerResize(drawerHeightRef.current);
        };

        window.addEventListener("pointermove", handlePointerMove);
        window.addEventListener("pointerup", handlePointerEnd);
        window.addEventListener("pointercancel", handlePointerEnd);

        return () => {
            window.removeEventListener("pointermove", handlePointerMove);
            window.removeEventListener("pointerup", handlePointerEnd);
            window.removeEventListener("pointercancel", handlePointerEnd);
        };
    }, [finishDrawerResize, isResizingDrawer]);

    return {
        drawerOpen,
        drawerHeight,
        isResizingDrawer,
        minHeight: DEFAULT_MIN_HEIGHT,
        maxHeightVh: DEFAULT_MAX_HEIGHT_VH,
        closeDrawer,
        handleOpenDrawer,
        handleDrawerResizeStart,
        resetDrawerHeight,
    };
};
